"use client";

import React, { useRef } from "react";
import { ChevronDown } from "lucide-react";

/**
 * ScrollCue — a bouncing down-arrow shown at the bottom of a
 * full-screen hero. Clicking it smooth-scrolls past the whole hero to
 * the next section.
 *
 * Finds its nearest `[data-scroll-cue-root]` (or <section>) ancestor at
 * click time and scrolls to its bottom edge (= top of the following
 * content). The desktop header is in normal flow (scrolls away), so no
 * offset needed.
 *
 * `variant="labeled"` is the UMMG-style cue: a small "SCROLL" caption over
 * a ringed bouncing chevron. `className` overrides the default bottom
 * offset (e.g. to clear a carousel's dots).
 */
export function ScrollCue({
  label = "Scroll to content",
  caption = "Scroll",
  variant = "icon",
  className = "bottom-5",
}: {
  label?: string;
  caption?: string;
  variant?: "icon" | "labeled";
  className?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    const section = ref.current?.closest("[data-scroll-cue-root], section");
    if (!section) return;
    const target = section.getBoundingClientRect().bottom + window.scrollY;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced) {
      window.scrollTo(0, target);
      return;
    }

    // Manual rAF tween (easeInOutQuad). Avoids relying on native
    // `behavior:'smooth'`, which some environments don't animate.
    const start = window.scrollY;
    const dist = target - start;
    const duration = 650;
    let t0: number | null = null;
    const step = (ts: number) => {
      if (t0 === null) t0 = ts;
      const p = Math.min(1, (ts - t0) / duration);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      window.scrollTo(0, start + dist * eased);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  if (variant === "labeled") {
    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        aria-label={label}
        className={`group absolute left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-1.5 text-white/70 transition-colors hover:text-white ${className}`}
      >
        <span
          className="text-[10px] uppercase"
          style={{ letterSpacing: "0.24em", fontFamily: "ui-monospace, Menlo, monospace" }}
        >
          {caption}
        </span>
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 transition-colors group-hover:border-[color:var(--color-accent)] group-hover:text-[color:var(--color-accent)]">
          <ChevronDown className="h-[18px] w-[18px] motion-safe:animate-bounce" strokeWidth={2} />
        </span>
      </button>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      aria-label={label}
      className="absolute bottom-5 left-1/2 z-20 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full text-white ring-1 ring-white/40 transition-colors hover:bg-white/15 motion-safe:animate-bounce"
      style={{ background: "rgba(255,255,255,0.08)" }}
    >
      <ChevronDown className="h-6 w-6" strokeWidth={2.25} />
    </button>
  );
}

export default ScrollCue;
